import * as React from 'react';
import { hot } from 'react-hot-loader';
import { useDispatch } from 'react-redux';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Alert,
  Spinner,
} from 'reactstrap';
import { handleCallback, getUserInfo } from '../../../helpers/asureid';
import * as AccountActions from '../../../actions/account/account';
import { REACT_APP_ASURE_ID_CLIENT_ID } from '../../../constants/globals';
import { setToken } from '../../../helpers/tokens';
import { DEFAULT_TOKEN_NAME } from '../../../constants';

const AsureIdCallbackPage: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<any>(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    const processCallback = async () => {
      try {
        const callbackUser = await handleCallback();

        console.log(
          'Callback user expires_at:',
          callbackUser.expires_at,
          'current time:',
          Date.now() / 1000
        );
        // Set the access token for persistence
        setToken(DEFAULT_TOKEN_NAME, callbackUser.access_token);
        // Get user info to retrieve scopes
        const userInfo = await getUserInfo();
        const policies = userInfo['custom:asure']?.policy || [];
        const matchingPolicies = policies.filter(
          // tslint:disable-next-line:no-shadowed-variable
          (policy: any) =>
            policy.applicationClientId === REACT_APP_ASURE_ID_CLIENT_ID
        );
        const scopes = matchingPolicies.flatMap(
          // tslint:disable-next-line:no-shadowed-variable
          (policy: any) => policy.permissions || []
        );

        // Map user info to account data
        const rules = scopes.map((permission: string) => ({
          action: permission,
        }));
        const policy = { version: 'v1', rules };
        const accountData = {
          tenantId: userInfo['custom:asure']?.tenantId,
          id: userInfo['custom:asure']?.userId,
          email: userInfo.email,
          username: userInfo['custom:asure']?.username,
          givenName: userInfo.given_name,
          surname: userInfo.family_name,
          loadedFromToken: true,
          loadedFromDb: false,
          scope: scopes,
          policy, // policy object for SecurityPolicyAuthorizer
        };

        // Dispatch account data
        dispatch(AccountActions.setAccountData(accountData));

        // Store account data and auth method for persistence
        sessionStorage.setItem('accountData', JSON.stringify(accountData));
        localStorage.setItem('authMethod', 'asure-id');

        // Redirect to previous href like original SSO
        const previousHref =
          window.sessionStorage.getItem('oauth.previous-href') ||
          '/sso-configuration';
        window.location.replace(previousHref);

        // Set local user for display (though we redirect, keep for consistency)
        if (isMountedRef.current) {
          setUser(callbackUser);
        }

        if (isMountedRef.current) {
          setLoading(false);
        }
      } catch (err) {
        console.log('Unable to handle redirect', err);
        if (isMountedRef.current) {
          setError((err as Error).message || 'Unknown error occurred');
          setLoading(false);
        }
      }
    };

    processCallback();

    return () => {
      isMountedRef.current = false;
    };
  }, [dispatch]);

  if (loading) {
    return (
      <Row className="justify-content-center mt-5">
        <Col md={8} lg={6} className="text-center">
          <Spinner color="primary" />
          <p className="mt-3">Processing authentication...</p>
        </Col>
      </Row>
    );
  }

  if (error) {
    return (
      <Row className="justify-content-center mt-5">
        <Col md={8} lg={6}>
          <Alert color="danger">
            <h4>Authentication Error</h4>
            <p>{error}</p>
          </Alert>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="justify-content-center mt-5">
      <Col md={8} lg={6}>
        {user ? (
          <Card>
            <CardHeader>
              <h4>Authentication Successful</h4>
            </CardHeader>
            <CardBody>
              <div className="mb-3">
                <strong>User Information</strong>
              </div>
              {user.profile && (
                <>
                  {user.profile.name && (
                    <div className="mb-2">
                      <strong>Name:</strong> {user.profile.name}
                    </div>
                  )}
                  {user.profile.email && (
                    <div className="mb-2">
                      <strong>Email:</strong> {user.profile.email}
                    </div>
                  )}
                  {user.profile.sub && (
                    <div className="mb-2">
                      <strong>ID:</strong> {user.profile.sub}
                    </div>
                  )}
                </>
              )}
              <div className="mt-3">
                <strong>Token Information</strong>
              </div>
              <div className="mb-2">
                <strong>Token Type:</strong> {user.token_type}
              </div>
              {user.expires_at && (
                <div className="mb-2">
                  <strong>Expires:</strong>{' '}
                  {new Date(user.expires_at * 1000).toLocaleString()}
                </div>
              )}
              <div className="mb-2">
                <strong>Scopes:</strong> {user.scope}
              </div>
            </CardBody>
          </Card>
        ) : (
          <Alert color="warning">
            <h4>Authentication Complete</h4>
            <p>
              Authenticated successfully, but no user information is available.
            </p>
          </Alert>
        )}
      </Col>
    </Row>
  );
};

export default hot(module)(AsureIdCallbackPage);
